import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCached, clearCache } from '../functions/swipeCache';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/ranking.png';

const sortByMonth   = (u) => [...u].sort((a,b) => b.month !== a.month ? b.month - a.month : b.average !== a.average ? b.average - a.average : b.wines - a.wines);
const sortByAverage = (u) => [...u].sort((a,b) => b.average !== a.average ? b.average - a.average : b.wines !== a.wines ? b.wines - a.wines : b.month - a.month);
const sortByWines   = (u) => [...u].sort((a,b) => b.wines !== a.wines ? b.wines - a.wines : b.average !== a.average ? b.average - a.average : b.month - a.month);

function RkIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':    return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'gangs':   return <svg {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
        case 'person':  return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'trophy':  return <svg {...p}><path d="M6 9V4h12v5a6 6 0 0 1-12 0Z"/><path d="M6 4H3v3a3 3 0 0 0 3 3M18 4h3v3a3 3 0 0 1-3 3"/><path d="M9 21h6M12 15v6"/></svg>;
        case 'chevron': return <svg {...p}><path d="m6 9 6 6 6-6"/></svg>;
        default: return null;
    }
}

function PodiumSlot({ user, rank, sortMode }) {
    if (!user) return <div className="rk-podium-slot rk-podium-empty" />;
    const gang = user.relevant_winegang?.name || '';
    const score = sortMode === 'monthly'
        ? user.month
        : sortMode === 'general'
            ? parseFloat(user.average || 0).toFixed(1) + '%'
            : user.wines;

    return (
        <div className={`rk-podium-slot rk-p${rank}`}>
            <div className="rk-podium-medal">
                <span className="rk-podium-num">{rank}</span>
            </div>
            <div className="rk-podium-name">{user.username}</div>
            {gang && <div className="rk-podium-gang">{gang}</div>}
            <div className="rk-podium-score">{score}</div>
            <div className="rk-podium-base" />
        </div>
    );
}

export default function Ranking() {
    const navigate = useNavigate();
    const { t: tl } = useTranslation();
    const [userData, setUserData]       = useState([]);
    const [sortMode, setSortMode]       = useState('monthly');
    const [ready, setReady]             = useState(false);
    const [tooltip, setTooltip]         = useState(null);
    const [fetchError, setFetchError]   = useState(false);
    const [retryKey, setRetryKey]       = useState(0);

    // Slide-in direction from swipe
    const swipeClass = useRef(
        sessionStorage.getItem('swipeDir') === 'from-left' ? ' rk-swipe-in' : ''
    );
    useEffect(() => { sessionStorage.removeItem('swipeDir'); }, []);

    useEffect(() => {
        setFetchError(false);
        const url = `${process.env.REACT_APP_API_URL}/users`;
        const cached = getCached(url);
        if (cached) {
            setUserData(sortByMonth(cached));
            clearCache(url);
            requestAnimationFrame(() => setReady(true));
            return;
        }
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    setUserData(sortByMonth(JSON.parse(this.responseText)));
                    requestAnimationFrame(() => setReady(true));
                } else {
                    setFetchError(true);
                }
            }
        };
        xhr.send();
    }, [retryKey]);

    // Reverse swipe → back to home
    const swipeTouchX = useRef(null);
    const swipeTouchY = useRef(null);
    const handleTouchStart = (e) => { swipeTouchX.current = e.touches[0].clientX; swipeTouchY.current = e.touches[0].clientY; };
    const handleTouchEnd   = (e) => {
        if (swipeTouchX.current === null) return;
        const dx = e.changedTouches[0].clientX - swipeTouchX.current;
        const dy = e.changedTouches[0].clientY - swipeTouchY.current;
        swipeTouchX.current = null;
        if (window.innerWidth >= 1100) return;
        if (Math.abs(dy) > Math.abs(dx) * 0.8) return;
        if (dx > 75) { sessionStorage.setItem('swipeDir', 'from-right'); navigate('/'); }
    };

    const sorted = sortMode === 'monthly'
        ? sortByMonth(userData)
        : sortMode === 'general'
            ? sortByAverage(userData)
            : sortByWines(userData);

    const handleSort = (mode) => setSortMode(mode);

    const tooltipText = {
        monthly: tl('ranking.month_info'),
        general: tl('ranking.general_info'),
        wines:   tl('ranking.wines_info'),
    };

    const MEDAL = ['#d4a017', '#9baab5', '#c47b32'];

    return (
        <div
            className={`rk-stage${ready ? ' rk-ready' : ''}${swipeClass.current}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div className="rk-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="rk-vignette" />
            <div className="rk-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{tl('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{tl('common.retry')}</button>
                </div>
            )}

            {/* Header */}
            <div className="rk-header">
                <button className="rk-back-btn" onClick={() => navigate(-1)} aria-label={tl('common.back')}>
                    <RkIcon name="back" size={18} />
                </button>
                <div className="rk-title-wrap">
                    <RkIcon name="trophy" size={16} />
                    <h1 className="rk-title">{tl('ranking.title')}</h1>
                </div>
                <div className="rk-mode-tabs">
                    <Link to="/ranking" className="rk-mode-tab rk-mode-active">
                        <RkIcon name="person" size={14} />
                        <span>{tl('common.individual')}</span>
                    </Link>
                    <Link to="/ranking-gangs" className="rk-mode-tab">
                        <RkIcon name="gangs" size={14} />
                        <span>{tl('common.winegangs')}</span>
                    </Link>
                </div>
            </div>

            {/* Podium */}
            <div className="rk-podium">
                <PodiumSlot user={sorted[1]} rank={2} sortMode={sortMode} />
                <PodiumSlot user={sorted[0]} rank={1} sortMode={sortMode} />
                <PodiumSlot user={sorted[2]} rank={3} sortMode={sortMode} />
            </div>

            {/* Table panel */}
            <div className="rk-panel">
                {/* Sort bar */}
                <div className="rk-sort-bar">
                    {[
                        { key: 'monthly', label: tl('common.month') },
                        { key: 'general', label: tl('common.level') },
                        { key: 'wines',   label: tl('common.wines') },
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            className={`rk-sort-btn${sortMode === key ? ' rk-sort-active' : ''}`}
                            onClick={() => handleSort(key)}
                            onMouseEnter={() => setTooltip(key)}
                            onMouseLeave={() => setTooltip(null)}
                        >
                            {label}
                            {sortMode !== key && <RkIcon name="chevron" size={11} />}
                        </button>
                    ))}
                </div>
                {tooltip && (
                    <div className="rk-tooltip">{tooltipText[tooltip]}</div>
                )}

                {/* Table */}
                <div className="rk-table">
                    <div className="rk-thead">
                        <span>{tl('common.position')}</span>
                        <span>{tl('ranking.name_gang')}</span>
                        <span className={sortMode === 'monthly'  ? 'rk-col-active' : ''}>{tl('common.month')}</span>
                        <span className={sortMode === 'general'  ? 'rk-col-active' : ''}>{tl('common.level')}</span>
                        <span className={sortMode === 'wines'    ? 'rk-col-active' : ''}>{tl('common.wines')}</span>
                    </div>
                    <div className="rk-tbody">
                        {sorted.map((user, i) => {
                            const gang = user.relevant_winegang?.name || '';
                            const isTop3 = i < 3;
                            return (
                                <button
                                    key={user.id}
                                    className={`rk-row${isTop3 ? ' rk-row-top' : ''}`}
                                    style={{ animationDelay: `${i * 0.04}s` }}
                                    onClick={() => navigate(`/user/${user.id}`)}
                                >
                                    <span className="rk-pos">
                                        {isTop3 && (
                                            <span className="rk-medal-dot" style={{ background: MEDAL[i] }} />
                                        )}
                                        {i + 1}
                                    </span>
                                    <span className="rk-name">
                                        <span className="rk-username">{user.username}</span>
                                        {gang && <span className="rk-gang">{gang}</span>}
                                    </span>
                                    <span className={`rk-val${sortMode === 'monthly'  ? ' rk-val-active' : ''}`}>{user.month}</span>
                                    <span className={`rk-val${sortMode === 'general'  ? ' rk-val-active' : ''}`}>{parseFloat(user.average || 0).toFixed(1)}%</span>
                                    <span className={`rk-val${sortMode === 'wines'    ? ' rk-val-active' : ''}`}>{user.wines}</span>
                                </button>
                            );
                        })}
                        {sorted.length === 0 && (
                            <div className="rk-empty">{tl('common.loading')}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
